import { useState } from 'react';
import { ExternalLink, FileText, Pencil } from 'lucide-react';
import { formatarMoeda, type EtapaDto, type ProcessoDetalheDto } from '@nexital/shared';
import { Dialogo } from '../design-system/Dialogo';
import { useLicitacao } from './contexto';
import { Modulo } from './Modulo';

const VAZIO = '—';

const situacaoDa = (indice: number, atual: number) => {
  if (indice < atual) return 'feita';
  return indice === atual ? 'atual' : 'futura';
};

/** Trilha de etapas: as anteriores à atual aparecem concluídas (RQ-LIC-002). */
function Etapas({ etapas, atual }: { etapas: EtapaDto[]; atual: string }) {
  const indiceAtual = etapas.findIndex((e) => e.uuid === atual);
  return (
    <ol className="sobre__etapas" aria-label="Etapas do processo">
      {etapas.map((etapa, i) => {
        const situacao = situacaoDa(i, indiceAtual);
        return (
          <li key={etapa.uuid} className={`sobre__etapa sobre__etapa--${situacao}`} aria-current={situacao === 'atual' ? 'step' : undefined}>
            <span className="sobre__marcador" aria-hidden />
            {etapa.nome}
          </li>
        );
      })}
    </ol>
  );
}

type Campos = { objeto: string; modalidade: string; criterioJulgamento: string; numeroProcesso: string; valorEstimado: string; linkPortal: string };

const camposDe = (p: ProcessoDetalheDto): Campos => ({
  objeto: p.objeto,
  modalidade: p.modalidade ?? '',
  criterioJulgamento: p.criterioJulgamento ?? '',
  numeroProcesso: p.numeroProcesso ?? '',
  valorEstimado: p.valorEstimado === null ? '' : String(p.valorEstimado),
  linkPortal: p.linkPortal ?? '',
});

const ROTULOS: [keyof Campos, string, string?][] = [
  ['objeto', 'Objeto'],
  ['modalidade', 'Modalidade'],
  ['criterioJulgamento', 'Critério de julgamento'],
  ['numeroProcesso', 'Número do processo'],
  ['valorEstimado', 'Valor estimado (R$)', 'number'],
  ['linkPortal', 'Link do portal de compras', 'url'],
];

function DialogoSobre({ processo, fechar }: { processo: ProcessoDetalheDto; fechar: () => void }) {
  const editar = useLicitacao((s) => s.editarProcesso);
  const [campos, definir] = useState(camposDe(processo));
  const texto = (v: string) => v.trim() || null;
  const salvar = async () => {
    const valor = campos.valorEstimado.trim() === '' ? null : Number(campos.valorEstimado.replace(',', '.'));
    const gravou = await editar({
      objeto: campos.objeto.trim(),
      modalidade: texto(campos.modalidade),
      criterioJulgamento: texto(campos.criterioJulgamento),
      numeroProcesso: texto(campos.numeroProcesso),
      valorEstimado: valor,
      linkPortal: texto(campos.linkPortal),
    });
    if (gravou) fechar();
  };
  return (
    <Dialogo titulo="Editar dados da licitação" rotuloConfirmar="Salvar" podeConfirmar={campos.objeto.trim() !== ''} aoConfirmar={() => void salvar()} aoCancelar={fechar}>
      {ROTULOS.map(([chave, rotulo, tipo]) => (
        <label key={chave} className="campo">
          {rotulo}
          <input type={tipo ?? 'text'} step={tipo === 'number' ? '0.01' : undefined} value={campos[chave]} onChange={(e) => definir({ ...campos, [chave]: e.target.value })} />
        </label>
      ))}
    </Dialogo>
  );
}

/** "Sobre esta licitação": objeto, dados do edital e etapas (referência B). */
export function ModuloSobre() {
  const dados = useLicitacao((s) => s.dados)!;
  const [editando, definirEditando] = useState(false);
  const p = dados.processo;
  const acoes = (
    <button type="button" className="botao-icone" onClick={() => definirEditando(true)} aria-label="Editar dados da licitação">
      <Pencil size={16} />
    </button>
  );
  return (
    <Modulo titulo="Sobre esta licitação" Icone={FileText} cor="lilas" acoes={acoes}>
      <dl className="sobre__dados">
        <div className="sobre__objeto">
          <dt>Objeto</dt>
          <dd>{p.objeto}</dd>
        </div>
        <div><dt>Modalidade</dt><dd>{p.modalidade ?? VAZIO}</dd></div>
        <div><dt>Critério de julgamento</dt><dd>{p.criterioJulgamento ?? VAZIO}</dd></div>
        <div><dt>Processo</dt><dd>{p.numeroProcesso ?? VAZIO}</dd></div>
        <div><dt>Valor estimado</dt><dd>{p.valorEstimado === null ? VAZIO : formatarMoeda(p.valorEstimado)}</dd></div>
      </dl>
      {p.linkPortal && (
        <a className="sobre__portal" href={p.linkPortal} target="_blank" rel="noreferrer">
          Portal de compras <ExternalLink size={14} aria-hidden />
        </a>
      )}
      <Etapas etapas={dados.etapas} atual={p.estadoUuid} />
      {editando && <DialogoSobre processo={p} fechar={() => definirEditando(false)} />}
    </Modulo>
  );
}
